import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';
import type {
  ApprovalRule,
  ApprovalRuleInput,
  ApprovalRuleUpdateInput,
  BillingScheduleItem,
  BillingScheduleItemInput,
  BillingScheduleItemUpdate,
  Contract,
  ContractFinanceDashboard,
  ContractRenewal,
  ContractUpdateInput,
  Paginated,
  RenewalInput,
  RenewalPipeline,
  ResolveApprovalResult,
} from './types';

/* ── Hooks de Contratos (R3C) ──────────────────────────────────────────
 * Consomem as rotas reais do backend em /api/v1:
 *   GET/PATCH  /contracts/:id
 *   GET/POST   /contracts/:id/billing-schedule (+ /generate, PATCH /:itemId)
 *   GET        /contracts/renewals/pipeline
 *   POST       /contracts/:id/renewals, /renewals/:id/approve|reject
 *   CRUD       /approval-rules + GET /approval-rules/resolve
 *   GET        /contracts/integration-dashboard
 */

/* ── Contrato: detalhe + edição ── */

export function useContract(contractId: string): ReturnType<typeof useQuery<Contract>> {
  return useQuery<Contract>({
    queryKey: ['sales', 'contracts', 'detail', contractId],
    queryFn: async () => apiClient.get<Contract>(`/contracts/${contractId}`),
    enabled: !!contractId,
  });
}

export function useUpdateContract(contractId: string) {
  const queryClient = useQueryClient();
  return useMutation<Contract, unknown, ContractUpdateInput, unknown>({
    mutationFn: (input: ContractUpdateInput) =>
      apiClient.patch<Contract>(`/contracts/${contractId}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'contracts', 'detail', contractId] });
      queryClient.invalidateQueries({ queryKey: ['crm', 'contracts'] });
    },
  });
}

/* ── Cronograma de faturamento ── */

export function useBillingSchedule(
  contractId: string,
): ReturnType<typeof useQuery<Paginated<BillingScheduleItem>>> {
  return useQuery<Paginated<BillingScheduleItem>>({
    queryKey: ['sales', 'contracts', 'billing-schedule', contractId],
    queryFn: async () =>
      apiClient.get<Paginated<BillingScheduleItem>>(
        `/contracts/${contractId}/billing-schedule`,
        { params: { limit: '200' } },
      ),
    enabled: !!contractId,
  });
}

export function useCreateBillingSchedule(contractId: string) {
  const queryClient = useQueryClient();
  return useMutation<BillingScheduleItem[], unknown, BillingScheduleItemInput[], unknown>({
    mutationFn: (items: BillingScheduleItemInput[]) =>
      apiClient.post<BillingScheduleItem[]>(`/contracts/${contractId}/billing-schedule`, {
        items,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sales', 'contracts', 'billing-schedule', contractId],
      });
      queryClient.invalidateQueries({ queryKey: ['sales', 'contracts', 'integration-dashboard'] });
    },
  });
}

export function useGenerateBillingSchedule(contractId: string) {
  const queryClient = useQueryClient();
  return useMutation<BillingScheduleItem[], unknown, { installments: number; dueDays?: number }, unknown>({
    mutationFn: (input: { installments: number; dueDays?: number }) =>
      apiClient.post<BillingScheduleItem[]>(
        `/contracts/${contractId}/billing-schedule/generate`,
        input,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sales', 'contracts', 'billing-schedule', contractId],
      });
    },
  });
}

export function useUpdateBillingScheduleItem(contractId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    BillingScheduleItem,
    unknown,
    { itemId: string; input: BillingScheduleItemUpdate },
    unknown
  >({
    mutationFn: ({ itemId, input }) =>
      apiClient.patch<BillingScheduleItem>(
        `/contracts/${contractId}/billing-schedule/${itemId}`,
        input,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sales', 'contracts', 'billing-schedule', contractId],
      });
      queryClient.invalidateQueries({ queryKey: ['sales', 'contracts', 'integration-dashboard'] });
    },
  });
}

/* ── Renovação de contratos ── */

export function useRenewalPipeline(): ReturnType<typeof useQuery<RenewalPipeline>> {
  return useQuery<RenewalPipeline>({
    queryKey: ['sales', 'contracts', 'renewals', 'pipeline'],
    queryFn: async () => apiClient.get<RenewalPipeline>('/contracts/renewals/pipeline'),
  });
}

/** Criar proposta: o contrato é escolhido no formulário, então entra no input. */
export interface CreateRenewalInput extends RenewalInput {
  contractId: string;
}

export function useCreateRenewal() {
  const queryClient = useQueryClient();
  return useMutation<ContractRenewal, unknown, CreateRenewalInput, unknown>({
    mutationFn: ({ contractId, ...input }: CreateRenewalInput) =>
      apiClient.post<ContractRenewal>(`/contracts/${contractId}/renewals`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'contracts', 'renewals'] });
    },
  });
}

export function useApproveRenewal() {
  const queryClient = useQueryClient();
  return useMutation<ContractRenewal, unknown, string, unknown>({
    mutationFn: (renewalId: string) =>
      apiClient.post<ContractRenewal>(`/renewals/${renewalId}/approve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'contracts', 'renewals'] });
    },
  });
}

export function useRejectRenewal() {
  const queryClient = useQueryClient();
  return useMutation<ContractRenewal, unknown, { renewalId: string; reason: string }, unknown>({
    mutationFn: ({ renewalId, reason }) =>
      apiClient.post<ContractRenewal>(`/renewals/${renewalId}/reject`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'contracts', 'renewals'] });
    },
  });
}

/* ── Regras de alçada ── */

export interface ApprovalRuleListParams {
  documentType?: string;
  active?: boolean;
  page?: number;
  limit?: number;
}

export function useApprovalRules(
  params: ApprovalRuleListParams,
): ReturnType<typeof useQuery<Paginated<ApprovalRule>>> {
  return useQuery<Paginated<ApprovalRule>>({
    queryKey: ['sales', 'contracts', 'approval-rules', params],
    queryFn: async () =>
      apiClient.get<Paginated<ApprovalRule>>('/approval-rules', {
        params: {
          ...(params.documentType ? { documentType: params.documentType } : {}),
          ...(params.active !== undefined ? { active: String(params.active) } : {}),
          page: String(params.page ?? 1),
          limit: String(params.limit ?? 50),
        },
      }),
  });
}

export function useCreateApprovalRule() {
  const queryClient = useQueryClient();
  return useMutation<ApprovalRule, unknown, ApprovalRuleInput, unknown>({
    mutationFn: (input: ApprovalRuleInput) =>
      apiClient.post<ApprovalRule>('/approval-rules', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'contracts', 'approval-rules'] });
    },
  });
}

export interface UpdateApprovalRuleInput {
  ruleId: string;
  input: ApprovalRuleUpdateInput;
}

export function useUpdateApprovalRule() {
  const queryClient = useQueryClient();
  return useMutation<ApprovalRule, unknown, UpdateApprovalRuleInput, unknown>({
    mutationFn: ({ ruleId, input }: UpdateApprovalRuleInput) =>
      apiClient.patch<ApprovalRule>(`/approval-rules/${ruleId}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'contracts', 'approval-rules'] });
    },
  });
}

export function useDeleteApprovalRule() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string, unknown>({
    mutationFn: (ruleId: string) => apiClient.delete<void>(`/approval-rules/${ruleId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'contracts', 'approval-rules'] });
    },
  });
}

export interface ResolveApprovalQuery {
  documentType: string;
  value: number;
  costCenter?: string;
}

export function useResolveApprovalRule(query: ResolveApprovalQuery | null) {
  return useQuery<ResolveApprovalResult>({
    queryKey: ['sales', 'contracts', 'approval-rules', 'resolve', query],
    queryFn: async () =>
      apiClient.get<ResolveApprovalResult>('/approval-rules/resolve', {
        params: {
          documentType: query?.documentType ?? '',
          value: String(query?.value ?? 0),
          ...(query?.costCenter ? { costCenter: query.costCenter } : {}),
        },
      }),
    enabled: query !== null,
  });
}

/* ── Dashboard de integração contrato-financeiro ── */

export function useContractFinanceDashboard(): ReturnType<
  typeof useQuery<ContractFinanceDashboard>
> {
  return useQuery<ContractFinanceDashboard>({
    queryKey: ['sales', 'contracts', 'integration-dashboard'],
    queryFn: async () => apiClient.get<ContractFinanceDashboard>('/contracts/integration-dashboard'),
  });
}